import { useEffect, useState } from 'react'
import {
  Modal,
  TextInput,
  Select,
  Autocomplete,
  Switch,
  Button,
  Group,
  Stack,
  TagsInput,
  Alert,
  Text,
  List,
  Badge,
  Divider,
  ActionIcon,
  Table,
} from '@mantine/core'
import { IconAlertTriangle, IconPlus, IconTrash } from '@tabler/icons-react'
import { Client, CreateClientData, UpdateClientData, ClientDuplicateWarning } from '../../api/clientsApi'
import { Application } from '../../api/applicationsApi'

interface ClientFormProps {
  opened: boolean
  onClose: () => void
  onSubmit: (data: CreateClientData | UpdateClientData) => Promise<boolean>
  client?: Client | null
  duplicateWarning?: ClientDuplicateWarning | null
  onClearDuplicateWarning?: () => void
  applications: Application[]
}

interface FormErrors {
  name?: string
  type?: string
  aliases?: string
  externalIds?: string
}

interface ExternalIdRow {
  key: string
  value: string
}

function externalIdsToRows(externalIds: Record<string, string>): ExternalIdRow[] {
  return Object.entries(externalIds).map(([key, value]) => ({ key, value }))
}

function rowsToExternalIds(rows: ExternalIdRow[]): Record<string, string> {
  const result: Record<string, string> = {}
  for (const row of rows) {
    const k = row.key.trim()
    const v = row.value.trim()
    if (k && v) result[k] = v
  }
  return result
}

const CLIENT_TYPE_OPTIONS = [
  { value: 'studio', label: 'Studio' },
  { value: 'formatPartner', label: 'Format Partner' },
]

export function ClientForm({
  opened,
  onClose,
  onSubmit,
  client,
  duplicateWarning,
  onClearDuplicateWarning,
  applications,
}: ClientFormProps) {
  const isEdit = !!client

  const [name, setName] = useState('')
  const [type, setType] = useState<string>('studio')
  const [aliases, setAliases] = useState<string[]>([])
  const [externalIdRows, setExternalIdRows] = useState<ExternalIdRow[]>([])
  const [isActive, setIsActive] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const appIdOptions = applications.map((a) => ({
    value: a.appId,
    label: `${a.appId} — ${a.name}`,
  }))

  useEffect(() => {
    if (opened) {
      if (client) {
        setName(client.name)
        setType(client.type)
        setAliases(client.aliases)
        setExternalIdRows(externalIdsToRows(client.externalIds ?? {}))
        setIsActive(client.isActive)
      } else {
        setName('')
        setType('studio')
        setAliases([])
        setExternalIdRows([])
        setIsActive(true)
      }
      setErrors({})
      onClearDuplicateWarning?.()
    }
  }, [opened, client])

  const validate = (): boolean => {
    const newErrors: FormErrors = {}

    if (!name.trim()) {
      newErrors.name = 'Name is required'
    } else if (name.length > 200) {
      newErrors.name = 'Name must be 200 characters or less'
    }

    if (!isEdit && !type) {
      newErrors.type = 'Type is required'
    }

    if (aliases.length > 20) {
      newErrors.aliases = 'Maximum 20 aliases allowed'
    }

    const keys = externalIdRows.map((r) => r.key.trim()).filter(Boolean)
    if (new Set(keys).size !== keys.length) {
      newErrors.externalIds = 'External ID keys must be unique'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const buildExternalIds = () => rowsToExternalIds(externalIdRows)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setIsSubmitting(true)

    let data: CreateClientData | UpdateClientData
    if (isEdit) {
      data = { name: name.trim(), aliases, externalIds: buildExternalIds(), isActive } as UpdateClientData
    } else {
      data = { name: name.trim(), type: type as 'studio' | 'formatPartner', aliases, externalIds: buildExternalIds() } as CreateClientData
    }

    const success = await onSubmit(data)
    setIsSubmitting(false)
    if (success) onClose()
  }

  const handleConfirmCreate = async () => {
    setIsSubmitting(true)
    const data: CreateClientData = {
      name: name.trim(),
      type: type as 'studio' | 'formatPartner',
      aliases,
      externalIds: buildExternalIds(),
      confirmCreate: true,
    }
    const success = await onSubmit(data)
    setIsSubmitting(false)
    if (success) onClose()
  }

  const addExternalIdRow = () =>
    setExternalIdRows((rows) => [...rows, { key: '', value: '' }])

  const updateExternalIdRow = (index: number, field: 'key' | 'value', val: string) =>
    setExternalIdRows((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [field]: val } : row))
    )

  const removeExternalIdRow = (index: number) =>
    setExternalIdRows((rows) => rows.filter((_, i) => i !== index))

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Edit Client' : 'New Client'}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Warner Bros."
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              onClearDuplicateWarning?.()
            }}
            error={errors.name}
            maxLength={200}
          />

          <Select
            label="Type"
            placeholder="Select type"
            data={CLIENT_TYPE_OPTIONS}
            required
            disabled={isEdit}
            value={type}
            onChange={(v) => v && setType(v)}
            description={isEdit ? 'Type cannot be changed' : undefined}
            error={errors.type}
          />

          <TagsInput
            label="Aliases"
            placeholder="Type an alias and press Enter"
            value={aliases}
            onChange={(v) => {
              setAliases(v)
              onClearDuplicateWarning?.()
            }}
            description="Known abbreviations or alternate names (e.g., WB, Warner Brothers)"
            error={errors.aliases}
            clearable
          />

          <Divider label="External IDs" labelPosition="left" />

          {externalIdRows.length > 0 && (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>System</Table.Th>
                  <Table.Th>ID</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {externalIdRows.map((row, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Autocomplete
                        placeholder="e.g. flightplan"
                        data={appIdOptions}
                        value={row.key}
                        onChange={(val) => updateExternalIdRow(i, 'key', val)}
                        size="xs"
                        comboboxProps={{ withinPortal: true }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        placeholder="ID value"
                        value={row.value}
                        onChange={(e) => updateExternalIdRow(i, 'value', e.target.value)}
                        size="xs"
                      />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        onClick={() => removeExternalIdRow(i)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}

          {errors.externalIds && (
            <Text size="xs" c="red">{errors.externalIds}</Text>
          )}

          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={addExternalIdRow}
            w="fit-content"
          >
            Add External ID
          </Button>

          {isEdit && (
            <Switch
              label="Active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          )}

          {duplicateWarning && (
            <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="Potential Duplicates Found">
              <Stack gap="xs">
                <Text size="sm">The following existing clients look similar:</Text>
                <List size="sm" spacing="xs">
                  {duplicateWarning.matches.map((match) => (
                    <List.Item key={match._id}>
                      <Group gap="xs">
                        <Text fw={500}>{match.name}</Text>
                        {match.aliases.length > 0 && (
                          <Text size="xs" c="dimmed">({match.aliases.join(', ')})</Text>
                        )}
                        <Badge size="xs" variant="light">
                          {Math.round((1 - match.score) * 100)}% match
                        </Badge>
                      </Group>
                    </List.Item>
                  ))}
                </List>
                <Group mt="xs">
                  <Button size="xs" variant="filled" color="yellow" onClick={handleConfirmCreate} loading={isSubmitting}>
                    Create Anyway
                  </Button>
                  <Button size="xs" variant="default" onClick={() => onClearDuplicateWarning?.()}>
                    Go Back
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}

          {!duplicateWarning && (
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                {isEdit ? 'Save Changes' : 'Create Client'}
              </Button>
            </Group>
          )}
        </Stack>
      </form>
    </Modal>
  )
}
